import css from 'styled-jsx/css'
import { Dispatch, SetStateAction, ChangeEvent, useState, useEffect } from 'react'
import { LoginStep, InputStatus } from '../contact/enum'
import { ValidateControl } from '../shared/Input/ValidateControl'
import { useAuthService } from '../../services/auth/auth.service'
import getConfig from 'next/config'
import Loading from '../../components/shared/Loading'
import { useRedirect } from '@ui/hooks'

interface LoginProps {
  currentStep: string
  nextStep: (step?: any) => void
  setDisplayStatus: Dispatch<SetStateAction<boolean>>
  setSearchForm: Dispatch<SetStateAction<{ email: string; phone: string }>>
  setCurrentStep: Dispatch<SetStateAction<string>>
}

export const FillLoginForm: React.FC<LoginProps> = ({
  nextStep,
  setDisplayStatus,
  setSearchForm
}) => {
  //const router = useRouter()
  const redirect = useRedirect()
  const [loading, setLoading] = useState<boolean>(false)
  const [inputStatus, setInputStatus] = useState<string>(InputStatus.BLANK)
  const [inputValue, setInputValue] = useState<string>('')
  const [error, setError] = useState<string>('')
  const { login } = useAuthService(getConfig().publicRuntimeConfig.shophelpBasePathApi)
  const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im
  const detectPhoneRegex = /^[+]?[(]?[0-9]*[)]?[-\s.]?[0-9]*[-\s.]?[0-9]*$/im
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  async function onValueChange(value: string) {
    setInputValue(value)
    setInputStatus(InputStatus.NEED_CHECK)
  }

  async function toAskOrder() {
    setLoading(true)
    redirect(`contact/forgot`, {
      routerOptions: { scroll: false },
      query: { ref: 'widget-login-input' }
    })
  }

  async function onLogin() {
    //login via phone or email.
    setLoading(true)
    setInputValue(inputValue.trim().toLowerCase())
    let body: Types.LoginModel
    {
      inputStatus == InputStatus.VALID_PHONE
        ? (body = {
            phone: inputValue,
            send_sms: true
          })
        : (body = {
            email: inputValue,
            send_sms: false
          })
    }
    const result = await login(body).then((data) => {
      setLoading(false)
      if (data === undefined) {
        if (inputStatus == InputStatus.VALID_EMAIL) {
          setInputStatus(InputStatus.CANT_FIND_EMAIL)
          setError(`We couldn't find your email address`)
        }
        if (inputStatus == InputStatus.VALID_PHONE) {
          setInputStatus(InputStatus.CANT_FIND_PHONE)
          setError(`We couldn't find your phone number`)
        }
        return false
      }
      return true
    })
    if (result) {
      if (inputStatus == InputStatus.VALID_PHONE) {
        setSearchForm({ phone: inputValue, email: '' })
      }
      if (inputStatus == InputStatus.VALID_EMAIL) {
        setSearchForm({ phone: '', email: inputValue })
      }
      nextStep(LoginStep.CODE)
    }
  }

  useEffect(() => {
    if (inputStatus != InputStatus.NEED_CHECK) {
      return
    }
    ;(async () => {
      const inputTest = inputValue.trim().toLowerCase()
      if (inputTest.length <= 0) {
        setError('')
        setInputStatus(InputStatus.BLANK)
      } else if (inputTest.length > 0 && inputTest.length < 3) {
        setError('Cannot detect email address / phone number')
        setInputStatus(InputStatus.CANT_DETECT)
      } else {
        if (detectPhoneRegex.test(inputTest)) {
          //validate phone
          if (phoneRegex.test(inputTest)) {
            setError('')
            setInputStatus(InputStatus.VALID_PHONE)
          } else {
            setError('Phone number must have 10 number only. exclude +1')
            setInputStatus(InputStatus.INVALID_PHONE)
          }
        } else {
          //validate email
          if (emailRegex.test(inputTest)) {
            setError('')
            setInputStatus(InputStatus.VALID_EMAIL)
          } else {
            setError('Email address wrong format')
            setInputStatus(InputStatus.INVALID_EMAIL)
          }
        }
      }
    })()
  }, [inputStatus])

  useEffect(() => {
    ;(async () => {
      if (inputStatus == InputStatus.NEED_CHECK) {
        return
      }
      inputStatus == InputStatus.INVALID_EMAIL ||
      inputStatus == InputStatus.INVALID_PHONE ||
      inputStatus == InputStatus.CANT_DETECT ||
      inputStatus == InputStatus.CANT_FIND_EMAIL ||
      inputStatus == InputStatus.CANT_FIND_PHONE
        ? setDisplayStatus(true)
        : setDisplayStatus(false)
    })()
  }, [inputStatus])

  return (
    <>
      <style jsx>{compStyle}</style>
      {loading && <Loading isFullWidth />}
      <div className="login-form ml-auto mr-auto has-text-centered">
        <h1 id={'contact-us-title'} className="login-form__title mt-8 mb-4">
          Contact Us
        </h1>
        <p className="login-form__content is-flex is-align-items-center is-justify-content-center">
          If you wish to track, modify, or return your order, please fill out the form below to
          contact us.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            onLogin()
          }}
        >
          <div className="field mt-8">
            <div className={`control`}>
              <ValidateControl
                id={`login_sh_widget`}
                firstSubmit={true}
                title=""
                className={`input${error ? ' is-danger' : ''}`}
                placeholder="Enter email or phone number"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onValueChange(e.target.value.trim().toLowerCase())
                }}
                value={inputValue ?? ''}
                error={error}
              ></ValidateControl>
            </div>
          </div>
          <div className="field mb-8">
            <div className={`control`}>
              <button
                id={`login_sh_widget_button`}
                type="submit"
                className="login_sh_widget_button button is-primary button--cta is-fullwidth-mobile"
                style={{ width: '100%' }}
                disabled={
                  inputStatus != InputStatus.VALID_PHONE && inputStatus != InputStatus.VALID_EMAIL
                }
              >
                Next
              </button>
            </div>
          </div>
          <a
            id={`login_forgot_ord`}
            className={`is-link-custom login-sh-forgot-ord-link`}
            onClick={() => {
              setError('')
              toAskOrder()
            }}
          >
            I forgot my email/phone number.
          </a>
        </form>
      </div>
    </>
  )
}

const compStyle = css`
  .login-form {
    background: var(--white-100);
    box-shadow: 2px 4px 40px rgba(161, 178, 203, 0.25);
    border-radius: var(--round-4);
    padding: 4rem;
    max-width: 450px;

    font-size: 15px;
    line-height: 32px;

    &__title {
      font-size: 21px;
      font-weight: 600;
    }

    &__content {
      color: var(--indigo-7);
    }

    .icon.is-left {
      color: var(--indigo-7);
      &::after {
        content: '';
        position: absolute;
        border: 1px solid var(--indigo-3);
        height: 16px;
        right: 0;
      }
    }
    .control.has-icons-left .input {
      padding-left: 3rem;
    }
    :global(.input) {
      ::-webkit-input-placeholder {
        /* Chrome/Opera/Safari */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      ::-moz-placeholder {
        /* Firefox 19+ */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      :-ms-input-placeholder {
        /* IE 10+ */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      :-moz-placeholder {
        /* Firefox 18- */
        color: var(--indigo-6);
        opacity: 0.3;
      }
      &:focus {
        border: 1px solid #43c3ff;
        box-shadow: none;
      }
      &:hover {
        box-shadow: none;
      }
    }
  }

  .btn-submit {
    padding: 8px 24px;
    background: var(--white-100);
    border: 2px solid var(--secondary-50);
    box-sizing: border-box;
    border-radius: var(--round-4);
    color: var(--secondary-100);

    &:hover {
      background: var(--indigo-2);
      font-weight: 700;
    }
  }

  .is-link-custom {
    cursor: pointer;
    color: #2d9cdb;
    font-weight: 400;
  }
`
